import React, { createContext, useContext, useEffect, useRef } from 'react';
import { discoveryApi } from '../../api/discovery';
import { useUserStore } from '../../stores/userStore';
const Context = createContext({ observe: () => () => {}, record: () => {} });
export function RecommendationTracking({ userId, children, onImpression }) {
  const nodes = useRef(new Map());
  const sent = useRef(new Set());
  const queue = useRef([]);
  const timer = useRef(null);
  const observer = useRef(null);
  const callback = useRef(onImpression);
  callback.current = onImpression;
  const live = useRef(true);
  const actorIsCurrent = () =>
    live.current && userId && useUserStore.getState?.().user?.id === userId;
  const record = (events) => {
    if (!actorIsCurrent()) return;
    // Navigation may unmount this provider in the same click. Send actions now.
    if (events.some((event) => event.eventType !== 'RECOMMENDATION_IMPRESSION')) {
      void discoveryApi.recordEvents(events).catch(() => {});
      return;
    }
    queue.current.push(...events);
    if (!timer.current)
      timer.current = setTimeout(() => {
        timer.current = null;
        const batch = queue.current.splice(0);
        if (actorIsCurrent() && batch.length) void discoveryApi.recordEvents(batch).catch(() => {});
      }, 0);
  };
  useEffect(() => {
    live.current = true;
    const check = (entries) => {
      if (document.hidden) return;
      for (const entry of entries) {
        const event = nodes.current.get(entry.target);
        const box = entry.boundingClientRect,
          visible = entry.intersectionRect;
        if (
          !event ||
          !entry.isIntersecting ||
          !box.width ||
          !box.height ||
          visible.width / box.width < 0.5 ||
          visible.height / box.height < 0.5
        )
          continue;
        const key = JSON.stringify(event);
        if (sent.current.has(key)) continue;
        sent.current.add(key);
        callback.current?.(event);
        record([{ ...event, eventType: 'RECOMMENDATION_IMPRESSION' }]);
      }
    };
    if (typeof IntersectionObserver !== 'undefined') {
      observer.current = new IntersectionObserver(check, {
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: '-60px 0px -80px 0px',
      });
      nodes.current.forEach((_, node) => observer.current.observe(node));
    }
    const resume = () => {
      if (!document.hidden)
        nodes.current.forEach((_, node) => {
          observer.current?.unobserve(node);
          observer.current?.observe(node);
        });
    };
    document.addEventListener('visibilitychange', resume);
    return () => {
      live.current = false;
      observer.current?.disconnect();
      clearTimeout(timer.current);
      timer.current = null;
      queue.current = [];
      document.removeEventListener('visibilitychange', resume);
    };
    // The provider is keyed by account; callbacks use refs and never change the observer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  return (
    <Context.Provider
      value={{
        record,
        observe: (node, event) => {
          nodes.current.set(node, event);
          observer.current?.observe(node);
          return () => {
            nodes.current.delete(node);
            observer.current?.unobserve(node);
          };
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}
export const useRecommendationTracking = () => useContext(Context);
export function RecommendationSlot({ event, className, children }) {
  const ref = useRef(null);
  const context = useRecommendationTracking();
  const eventKey = JSON.stringify(event);
  useEffect(
    () => (event && ref.current ? context.observe(ref.current, event) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventKey],
  );
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
export function recommendationEvent(data, surface, section, item, position) {
  if (!data?.generatedAt || item.fallback) return undefined;
  return {
    surface,
    section,
    algorithmVersion: data.algorithmVersion,
    reason: item.reason,
    requestId: `${surface === 'HOME' ? 'home' : 'search'}:${data.algorithmVersion}:${data.generatedAt}`,
    resultPosition: position,
    ...(section === 'TRACKS'
      ? { trackId: item.id, albumId: item.albumId }
      : section === 'REVIEWS'
        ? { reviewId: item.reviewId, albumId: item.albumId }
        : section === 'PLAYLISTS'
          ? { playlistId: item.id, targetUserId: item.userId }
          : { albumId: item.id, artistId: item.artist?.id }),
  };
}
